import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { StorySectionKey } from "@/lib/orgStory";
import { STORY_SECTION_LABELS, storySectionAnchor } from "./storyLabels";

interface SectionPillsProps {
  sections: StorySectionKey[];
}

/** How long a pill click suppresses observer-driven active changes. */
const CLICK_SUPPRESS_MS = 800;

/**
 * Sticky anchor pills for the story's sections; the active pill follows the
 * scroll. Every browser API is touched inside an effect or a handler, never
 * during render, so the component is safe to prerender (spec §6.5).
 *
 * Top offset: the bright agent shell's AppTopBar is 60px tall plus its 1px
 * `border-b` (AppTopBar.tsx — `theme === "bright"` uses `h-[60px]`), so the
 * pills stick at 61px. `StorySection`'s `scroll-mt` is kept one pixel ahead
 * of that for the same reason.
 */
export default function SectionPills({ sections }: SectionPillsProps) {
  const [active, setActive] = useState<StorySectionKey | null>(null);
  const current = active && sections.includes(active) ? active : sections[0] ?? null;
  const key = sections.join("|");

  // Suppresses observer-driven active changes right after a pill click, so
  // the click's own target wins the race against whatever section happens to
  // be intersecting mid-scroll. Cleared once the target section is actually
  // intersecting, or after CLICK_SUPPRESS_MS, whichever comes first.
  const pendingClickRef = useRef<StorySectionKey | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const elements = sections
      .map((k) => document.getElementById(storySectionAnchor(k)))
      .filter((el): el is HTMLElement => !!el);
    if (elements.length === 0) return;

    // IntersectionObserver callbacks report only the entries whose
    // intersection state CHANGED since the previous callback, so a running
    // map of every section's last-known state is required to pick "the
    // first intersecting section in page order" on each batch.
    const intersecting = new Map<StorySectionKey, boolean>();

    // A short final section may never fully satisfy the observer's bottom
    // margin, so being scrolled to the very bottom of the page directly means
    // the last section is active — checked both from the scroll listener and
    // from inside the observer callback, so a late intersection batch (one
    // that was already in flight when the user reached the bottom) cannot
    // pull the active pill back to an earlier section.
    const isAtBottom = () =>
      window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const k = entry.target.getAttribute("data-story-section") as StorySectionKey | null;
          if (k) intersecting.set(k, entry.isIntersecting);
        }

        if (pendingClickRef.current) {
          if (intersecting.get(pendingClickRef.current)) {
            pendingClickRef.current = null;
            if (pendingTimeoutRef.current) {
              clearTimeout(pendingTimeoutRef.current);
              pendingTimeoutRef.current = null;
            }
          } else {
            // Still mid-scroll toward the clicked section — ignore this batch.
            return;
          }
        }

        if (isAtBottom() && sections.length > 0) {
          setActive(sections[sections.length - 1]);
          return;
        }

        const next = sections.find((k) => intersecting.get(k));
        if (next) setActive(next);
      },
      { rootMargin: "-140px 0px -55% 0px" },
    );
    elements.forEach((el) => observer.observe(el));

    const handleScroll = () => {
      // A pending click already knows which section it wants active; don't
      // let a stray bottom-of-page reading from the same gesture override it.
      if (pendingClickRef.current) return;
      if (isAtBottom() && sections.length > 0) setActive(sections[sections.length - 1]);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
      pendingClickRef.current = null;
    };
    // `key` stands in for `sections`, whose array identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (sections.length === 0) return null;

  const handleClick = (k: StorySectionKey) => {
    setActive(k);
    const el = document.getElementById(storySectionAnchor(k));
    if (!el) return;

    pendingClickRef.current = k;
    if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
    pendingTimeoutRef.current = setTimeout(() => {
      pendingClickRef.current = null;
    }, CLICK_SUPPRESS_MS);

    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView?.({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
    el.focus({ preventScroll: true });
  };

  return (
    <nav
      aria-label="Story sections"
      className="sticky top-[61px] z-20 -mx-1 overflow-x-auto bg-background/95 px-1 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80"
    >
      <ul className="flex min-w-max gap-2">
        {sections.map((k) => (
          <li key={k}>
            <button
              type="button"
              aria-current={current === k ? "location" : undefined}
              onClick={() => handleClick(k)}
              className={cn(
                "rounded-pill border px-3.5 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                current === k
                  ? "border-primary bg-primary-tint text-primary"
                  : "border-border bg-card text-ink-500 hover:bg-muted",
              )}
            >
              {STORY_SECTION_LABELS[k].pill}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
